// client/src/pages/adminpages/AdvertiserRefundPage.js
import React, { useState } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import PointRefundList from '../../components/admin/marketer/PointRefundList';
import '../../styles/admin/AdminSummary.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdvertiserRefundPage() {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // 승인/거절 처리 상태
  const [processing, setProcessing] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [message, setMessage] = useState('');

  // 선택 시 상세 정보 조회
  const handleSelect = async (item) => {
    setSelected(item);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/advertiser/refund/${item._id}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setDetail(data);
      } else {
        setDetail(null);
      }
    } catch (err) {
      console.error('handleSelect error:', err);
      setDetail(null);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    setSearch(searchInput);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      corporate: '법인',
      sole: '개인사업자',
      agency: '광고사',
      freelancer: '프리랜서'
    };
    return labels[type] || type;
  };

  // 승인 처리
  const handleApprove = async () => {
    if (!selected) return;
    
    setProcessing(true);
    setMessage('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/advertiser/refund/${selected._id}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await res.json();
      
      if (data.ok) {
        setMessage('✅ 환불이 승인되었습니다.');
        setShowApproveModal(false);
        
        // 목록 새로고침 및 상세 정보 초기화
        setTimeout(() => {
          setSelected(null);
          setDetail(null);
          setRefreshToken(t => t + 1);
          setMessage('');
        }, 1500);
      } else {
        setMessage(`❌ ${data.message || '승인 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('handleApprove error:', error);
      setMessage('❌ 승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 거절 처리
  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) {
      setMessage('❌ 거절 사유를 입력해주세요.');
      return;
    }
    
    setProcessing(true);
    setMessage('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/advertiser/refund/${selected._id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rejectReason: rejectReason.trim() })
      });
      
      const data = await res.json();
      
      if (data.ok) {
        setMessage('✅ 환불이 거절되었습니다.');
        setShowRejectModal(false);
        setRejectReason('');
        
        // 목록 새로고침 및 상세 정보 초기화
        setTimeout(() => {
          setSelected(null);
          setDetail(null);
          setRefreshToken(t => t + 1);
          setMessage('');
        }, 1500);
      } else {
        setMessage(`❌ ${data.message || '거절 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('handleReject error:', error);
      setMessage('❌ 거절 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout title="광고주 환불 요청 관리">
      <div className="admin-grid">
        {/* 왼쪽: 목록 */}
        <div className="admin-section" style={{ height: 'calc(100vh - 120px)' }}>
          {/* 검색 */}
          <div className="admin-list__search">
            <div className="admin-list__search-grid">
              <input
                className="admin-list__search-input admin-list__search-input--full"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="광고주 ID 또는 이름 검색"
              />
            </div>
            <div className="admin-list__search-actions">
              <button 
                className="admin-list__search-button admin-list__search-button--primary"
                onClick={handleSearch}
              >
                검색
              </button>
            </div>
          </div>

          {/* 목록 */}
          <PointRefundList
            interactive={true}
            onSelect={handleSelect}
            selectedId={selected?._id}
            refreshToken={refreshToken}
            search={search}
          />
        </div>

        {/* 오른쪽: 상세 정보 */}
        <div className="admin-section admin-section--wide" style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <h3 className="admin-summary__title">환불 요청 상세</h3>
          {!selected ? (
            <div className="admin-summary__empty">좌측에서 환불 요청을 선택하세요.</div>
          ) : loading ? (
            <div className="admin-summary__loading">불러오는 중...</div>
          ) : detail ? (
            <>
              {/* 광고주 정보 */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 12px 0', color: 'var(--admin-color-primary)' }}>광고주 정보</h4>
                <div style={{ lineHeight: 2, fontSize: '14px', color: 'var(--admin-text-body)' }}>
                  <div><strong>광고주 ID:</strong> {detail.marketer.userId}</div>
                  <div><strong>이름:</strong> {detail.marketer.userName}</div>
                  <div><strong>회원 유형:</strong> {getTypeLabel(detail.marketer.type)}</div>
                  <div><strong>연락처:</strong> {detail.marketer.callNumber}</div>
                  <div><strong>이메일:</strong> {detail.marketer.email}</div>
                  
                  {detail.marketer.companyName && (
                    <div><strong>회사명:</strong> {detail.marketer.companyName}</div>
                  )}
                  {detail.marketer.position && (
                    <div><strong>직급:</strong> {detail.marketer.position}</div>
                  )}
                  {detail.marketer.businessName && (
                    <div><strong>상호:</strong> {detail.marketer.businessName}</div>
                  )}
                  {detail.marketer.bizRegNo && (
                    <div><strong>사업자 등록번호:</strong> {detail.marketer.bizRegNo}</div>
                  )}
                </div>
              </div>

              {/* 환불 요청 정보 */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 12px 0', color: 'var(--admin-color-warning)' }}>환불 요청 정보</h4>
                <div style={{ lineHeight: 2, fontSize: '14px', color: 'var(--admin-text-body)' }}>
                  <div><strong>신청 포인트:</strong> {detail.refund.requestedPoints.toLocaleString()}pt</div>
                  <div><strong>환불 금액 (110%):</strong> <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--admin-color-primary)' }}>{detail.refund.refundAmount.toLocaleString()}원</span></div>
                  <div><strong>신청일시:</strong> {new Date(detail.refund.requestedAt).toLocaleString('ko-KR')}</div>
                  <div><strong>상태:</strong> {detail.refund.status === 'pending' ? '대기 중' : detail.refund.status}</div>
                </div>
              </div>

              {/* 안내 메시지 */}
              <div style={{
                background: '#fff3cd',
                border: '1px solid var(--admin-color-warning)',
                borderRadius: 6,
                padding: 12,
                marginTop: 16
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#856404' }}>📞 처리 안내</div>
                <div style={{ fontSize: 13, color: '#856404', lineHeight: 1.6 }}>
                  위 연락처로 광고주에게 연락하여 계좌 정보를 확인한 후,<br />
                  오프라인으로 환불 처리를 진행해주세요.
                </div>
              </div>

              {/* 메시지 표시 */}
              {message && (
                <div style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 6,
                  background: message.startsWith('✅') ? '#d4edda' : '#f8d7da',
                  color: message.startsWith('✅') ? 'var(--admin-color-success)' : 'var(--admin-color-danger)',
                  border: `1px solid ${message.startsWith('✅') ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {message}
                </div>
              )}

              {/* 승인/거절 버튼 */}
              <div className="admin-section__actions" style={{ marginTop: 24 }}>
                <button
                  className="admin-section__button"
                  onClick={() => setShowApproveModal(true)}
                  disabled={processing}
                  style={{ backgroundColor: processing ? '#ccc' : 'var(--admin-color-success)', borderColor: processing ? '#ccc' : 'var(--admin-color-success)' }}
                >
                  승인
                </button>
                <button
                  className="admin-section__button"
                  onClick={() => setShowRejectModal(true)}
                  disabled={processing}
                  style={{ backgroundColor: processing ? '#ccc' : 'var(--admin-color-danger)', borderColor: processing ? '#ccc' : 'var(--admin-color-danger)' }}
                >
                  거절
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* 승인 확인 모달 */}
      {showApproveModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            padding: 24,
            borderRadius: 8,
            maxWidth: 500,
            width: '90%',
            boxShadow: 'var(--admin-shadow-xl)'
          }}>
            <h3 style={{ marginTop: 0 }}>환불 승인 확인</h3>
            <div style={{ marginBottom: 20, lineHeight: 1.6 }}>
              <p><strong>광고주:</strong> {selected?.marketerUserId} ({selected?.marketerUserName})</p>
              <p><strong>신청 포인트:</strong> {selected?.requestedPoints?.toLocaleString()}pt</p>
              <p><strong>환불 금액:</strong> {selected?.refundAmount?.toLocaleString()}원</p>
              <p style={{ color: 'var(--admin-color-success)', fontWeight: 600, marginTop: 16 }}>
                환불을 승인하시겠습니까?
              </p>
              <p style={{ fontSize: 14, color: 'var(--admin-text-sub)' }}>
                승인 후에는 취소할 수 없습니다.
              </p>
            </div>
            <div className="admin-section__actions" style={{ justifyContent: 'flex-end' }}>
              <button
                className="admin-section__button admin-section__button--secondary"
                onClick={() => setShowApproveModal(false)}
                disabled={processing}
              >
                취소
              </button>
              <button
                className="admin-section__button"
                onClick={handleApprove}
                disabled={processing}
                style={{ backgroundColor: 'var(--admin-color-success)', borderColor: 'var(--admin-color-success)' }}
              >
                {processing ? '처리 중...' : '승인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 거절 사유 입력 모달 */}
      {showRejectModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            padding: 24,
            borderRadius: 8,
            maxWidth: 500,
            width: '90%',
            boxShadow: 'var(--admin-shadow-xl)'
          }}>
            <h3 style={{ marginTop: 0 }}>환불 거절</h3>
            <div style={{ marginBottom: 20 }}>
              <p><strong>광고주:</strong> {selected?.marketerUserId} ({selected?.marketerUserName})</p>
              <p><strong>신청 포인트:</strong> {selected?.requestedPoints?.toLocaleString()}pt</p>
              <p style={{ color: 'var(--admin-color-danger)', fontWeight: 600, marginTop: 16 }}>
                환불을 거절하시겠습니까?
              </p>
              <p style={{ fontSize: 14, color: 'var(--admin-text-sub)', marginBottom: 12 }}>
                거절 시 포인트가 복구되며, 광고주에게 거절 사유가 전달됩니다.
              </p>
              
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                거절 사유 <span style={{ color: 'var(--admin-color-danger)' }}>*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요 (예: 계좌 정보 불일치)"
                style={{
                  width: '100%',
                  minHeight: 100,
                  padding: '10px 12px',
                  fontSize: 14,
                  border: '1px solid var(--admin-border-color)',
                  borderRadius: 4,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                disabled={processing}
              />
            </div>
            <div className="admin-section__actions" style={{ justifyContent: 'flex-end' }}>
              <button
                className="admin-section__button admin-section__button--secondary"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={processing}
              >
                취소
              </button>
              <button
                className="admin-section__button"
                onClick={handleReject}
                disabled={processing || !rejectReason.trim()}
                style={{ backgroundColor: 'var(--admin-color-danger)', borderColor: 'var(--admin-color-danger)' }}
              >
                {processing ? '처리 중...' : '거절'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdvertiserRefundPage;
